import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { eopdAPI } from "../../../../../services/e-opd.api";
import { toast } from "react-toastify";

// Read-only blocks Visit History ke saath share hote hain
import {
    PatientBanner,
    T,
    VisitDetailSections,
    raw,
    statusChipOf,
} from "../../shared/VisitRecordUI";

// Styles
import "../OpdStepper.css";

/* ------------------------------------------------------------------ */
/* Print helpers — sirf yahi page print hota hai                       */
/* ------------------------------------------------------------------ */
const PRINT_AREA_ID = "visit-summary-print-area";
const PRINT_BODY = "opd-print-summary";
const PRINT_KEEP = "opd-print-keep";
const PRINT_HIDE = "opd-print-hide";

/** Print ke baad (ya agle print se pehle) saare marker classes hata deta hai. */
function clearPrintMarks() {
    document.body.classList.remove(PRINT_BODY);
    document
        .querySelectorAll(`.${PRINT_KEEP}, .${PRINT_HIDE}`)
        .forEach((el) => el.classList.remove(PRINT_KEEP, PRINT_HIDE));
}

/**
 * Sirf summary sheet ko print karta hai.
 * Sheet tak ka ancestor chain "keep", har level ke baaki siblings "hide".
 * Isse sirf yeh subtree print hoti hai aur node apni jagah par hi rehta hai,
 * to React ka DOM chhedna nahi padta.
 */
function printSummarySheet() {
    const sheet = document.getElementById(PRINT_AREA_ID);
    if (!sheet) {
        window.print();
        return;
    }

    clearPrintMarks();

    for (let node = sheet; node && node !== document.body; node = node.parentElement) {
        node.classList.add(PRINT_KEEP);
        const parent = node.parentElement;
        if (!parent) break;
        for (const sibling of parent.children) {
            if (sibling !== node) sibling.classList.add(PRINT_HIDE);
        }
    }
    document.body.classList.add(PRINT_BODY);

    const cleanup = () => {
        clearPrintMarks();
        window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);

    window.print();
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function VisitSummary() {
    const { displayId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [visit, setVisit] = useState(null);

    useEffect(() => {
        let active = true;
        const fetchSummary = async () => {
            try {
                setLoading(true);
                const res = await eopdAPI.getVisitSummary(displayId);
                if (active && res && res.success && res.visit) {
                    setVisit(res.visit);
                } else if (active) {
                    toast.error("Failed to load visit details.");
                }
            } catch (err) {
                console.error(err);
                if (active) toast.error("Failed to load visit details.");
            } finally {
                if (active) setLoading(false);
            }
        };

        if (displayId) {
            fetchSummary();
        }
        return () => {
            active = false;
        };
    }, [displayId]);

    const handleStartNewEntry = () => {
        localStorage.removeItem("beneficiaryDetails");
        localStorage.removeItem("orignalBeneficiaryData");
        navigate("/e-opd/beneficiary");
    };

    const { general = {}, patient = {} } = visit || {};

    if (loading) {
        return (
            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    if (!visit) {
        return (
            <Box className="opd-stepper-page">
                <Paper
                    variant="outlined"
                    sx={{
                        maxWidth: 460,
                        mx: "auto",
                        mt: 8,
                        p: 4,
                        textAlign: "center",
                        borderRadius: 3,
                        borderColor: T.border,
                    }}
                >
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: T.ink, mb: 0.5 }}>
                        Visit details not found
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: T.muted, mb: 3 }}>
                        {displayId ? `No record for ${displayId}.` : "No visit id was provided."}
                    </Typography>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<KeyboardArrowLeftIcon />}
                        onClick={() => navigate("/e-opd/beneficiary")}
                        sx={{ textTransform: "none", borderRadius: "8px", px: 3 }}
                    >
                        Back to Search
                    </Button>
                </Paper>
            </Box>
        );
    }

    const statusChip = statusChipOf(visit);

    return (
        <Box className="opd-stepper-page">
            {/* -------- Page header (screen only) -------- */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "flex-end" }}
                gap={2}
                sx={{ mb: 2.5, "@media print": { display: "none" } }}
            >
                <Box>
                    <Box className="opd-brand-header" sx={{ mb: 1.5 }}>
                        <Box className="opd-brand-logo-container">
                            <img
                                src="/images/e-opd-logo.png"
                                alt="e-OPD Logo"
                                className="opd-brand-logo"
                                onError={(e) => {
                                    e.target.style.display = "none";
                                }}
                            />
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
                        Visit Summary
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: T.muted, mt: 0.5 }}>
                        Completed Read-only health record for the current visit encounter details.
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1.5} flexShrink={0}>
                    <Button
                        variant="outlined"
                        startIcon={<PrintOutlinedIcon />}
                        onClick={printSummarySheet}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            px: 2.5,
                            borderColor: T.border,
                            color: T.label,
                            "&:hover": { borderColor: "#C9D2E0", backgroundColor: T.surface },
                        }}
                    >
                        Print Summary
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<AddIcon />}
                        onClick={handleStartNewEntry}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", px: 2.5 }}
                    >
                        Start New Entry
                    </Button>
                </Stack>
            </Stack>

            {/* -------- Summary sheet -------- */}
            <Paper
                elevation={0}
                id={PRINT_AREA_ID}
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${T.border}`,
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    boxShadow: "0 4px 20px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.02)",
                    "@media print": { border: 0, boxShadow: "none", borderRadius: 0 },
                }}
            >
                {/* Patient banner */}
                <PatientBanner patient={patient}>
                    <Stack spacing={0.75} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                        <Tooltip title="Visit record number">
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: T.ink,
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                    letterSpacing: ".02em",
                                }}
                            >
                                {raw(visit.display_id || displayId)}
                            </Typography>
                        </Tooltip>
                        <Typography sx={{ fontSize: 12.5, color: T.label }}>
                            {raw(general.visit_datetime)}
                        </Typography>
                        <Chip
                            label={statusChip.label}
                            size="small"
                            sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 700,
                                color: statusChip.fg,
                                backgroundColor: statusChip.bg,
                            }}
                        />
                    </Stack>
                </PatientBanner>

                {/* Body */}
                <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <VisitDetailSections visit={visit} />

                    {/* Print-only footer */}
                    <Box sx={{ display: "none", "@media print": { display: "block", mt: 3 } }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography sx={{ fontSize: 11, color: T.muted }}>
                            {raw(general.facility_name)} · {raw(visit.display_id || displayId)} ·{" "}
                            {raw(general.visit_datetime)}
                        </Typography>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}
